import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Gauge, MapPin, Phone } from "lucide-react";
import { BUSINESS } from "@/constants/business";

const EASE = [0.16, 1, 0.3, 1];
const LINES = ["PRECISION", "MECHANICS.", "AT YOUR DOOR."];
const HERO_IMG =
    "https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxtZWNoYW5pYyUyMHJlcGFpcmluZyUyMGNhciUyMGVuZ2luZXxlbnwwfHx8fDE3OTA1NjQzNzZ8MA&ixlib=rb-4.1.0&q=85";

function Counter({ target, suffix = "", decimals = 0 }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true });
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (!inView) return;
        const start = performance.now();
        const duration = 1400;
        let raf;
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(target * eased);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, target]);
    return (
        <span ref={ref}>
            {value.toFixed(decimals)}
            {suffix}
        </span>
    );
}

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

    return (
        <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_70%_20%,rgba(245,158,11,0.12),transparent)]" />
            <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 sm:pt-28 sm:pb-32 grid lg:grid-cols-12 gap-12 items-center relative">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        data-testid="hero-dispatch-badge"
                        className="inline-flex items-center gap-2.5 border border-amber-500/30 bg-amber-500/10 rounded-full px-4 py-1.5 mb-8"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                        </span>
                        <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-amber-400">
                            Dispatch Active in Gympie Region — Same-Day Service
                        </span>
                    </motion.div>

                    <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.92] text-white text-5xl sm:text-7xl lg:text-8xl">
                        {LINES.map((line, i) => (
                            <span key={line} className="block overflow-hidden pb-1">
                                <motion.span
                                    className="block"
                                    initial={{ y: "110%" }}
                                    animate={{ y: "0%" }}
                                    transition={{ duration: 0.9, ease: EASE, delay: 0.15 + i * 0.16 }}
                                    data-testid={`hero-headline-line-${i + 1}`}
                                >
                                    {i === 2 ? <span className="text-amber-500">{line}</span> : line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.7 }}
                        className="mt-8 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
                        data-testid="hero-subtext"
                    >
                        Master-certified mobile mechanics delivering workshop-grade diagnostics, brake rebuilds and
                        scheduled servicing to your driveway, workplace or farm gate across Gympie and the Cooloola
                        Coast. No tow trucks. No waiting rooms.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
                        className="mt-10 flex flex-wrap gap-4"
                    >
                        <motion.a
                            href="#booking"
                            data-testid="hero-book-service-button"
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            className="bg-amber-500 text-[#0B0C0E] font-bold uppercase tracking-widest text-sm rounded-full px-8 py-4 hover:bg-amber-400 transition-colors duration-200 inline-flex items-center gap-2"
                        >
                            <MapPin className="w-4 h-4" /> Book On-Site Service
                        </motion.a>
                        <motion.a
                            href={BUSINESS.phoneTel}
                            data-testid="hero-call-button"
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            className="border border-[#383E4C] text-white font-bold uppercase tracking-widest text-sm rounded-full px-8 py-4 hover:border-amber-500/60 hover:text-amber-400 transition-colors duration-200 inline-flex items-center gap-2"
                        >
                            <Phone className="w-4 h-4" /> Call to Book
                        </motion.a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.05 }}
                        className="mt-14"
                        data-testid="hero-stats"
                    >
                        <div>
                            <div className="font-display text-3xl sm:text-4xl font-bold text-white">
                                <Counter target={30} suffix="m" decimals={0} />
                            </div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mt-1">
                                Oil Change Done In
                            </div>
                        </div>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1, ease: EASE, delay: 0.5 }}
                    className="lg:col-span-5 relative"
                >
                    <div className="relative rounded-2xl overflow-hidden border border-[#383E4C]" data-testid="hero-image-frame">
                        <motion.img
                            src={HERO_IMG}
                            alt="Master technician calibrating high-performance engine bay"
                            style={{ y: imgY, scale: 1.15 }}
                            className="w-full h-[420px] sm:h-[520px] object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-transparent to-black/40" />
                        <div className="absolute top-4 left-4 text-[10px] font-mono text-zinc-400 tracking-widest">
                            26.1907° S / 152.6655° E — MOBILE UNIT 04
                        </div>
                        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between bg-[#0B0C0E]/80 backdrop-blur-xl border border-white/10 rounded-xl px-4 py-3" data-testid="hero-telemetry-card">
                            <div className="flex items-center gap-3">
                                <Gauge className="w-5 h-5 text-amber-500" />
                                <div>
                                    <div className="text-xs font-mono text-white uppercase tracking-widest">Unit 04 — En Route</div>
                                    <div className="text-[10px] font-mono text-zinc-500">OEM diagnostics loaded</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
